"use client";

import { useState } from "react";

type Props = { next: string; configured: boolean };

export default function LoginForm({ next, configured }: Props) {
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [show, setShow] = useState(false);
  const [pending, setPending] = useState(false);

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    setPending(true);
    setError(null);

    try {
      const response = await fetch("/api/auth/login", {
        method: "POST",
        credentials: "same-origin",
        cache: "no-store",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ password }),
      });
      const payload = (await response.json()) as { ok?: boolean; error?: string };

      if (!response.ok || !payload.ok) {
        setError(payload.error ?? "Не удалось войти");
        return;
      }

      const sessionResponse = await fetch("/api/auth/session", {
        credentials: "same-origin", cache: "no-store",
      });
      if (!sessionResponse.ok) {
        setError("Не удалось проверить сессию. Обновите страницу и повторите вход.");
        return;
      }
      const session = await sessionResponse.json() as {authenticated?: boolean};
      if (!session.authenticated) {
        setError("Пароль принят, но сессия не сохранилась. Разрешите cookie для этого сайта и очистите его старые cookie. Входите и открывайте админку по одному адресу: www.ligastats.ru либо ligastats.ru.");
        return;
      }
      // Полная навигация исключает использование сохранённого до входа RSC-редиректа.
      const destination = new URL(next, window.location.origin);
      window.location.replace(destination.origin === window.location.origin ? destination.href : "/admin");
    } catch {
      setError("Ошибка сети. Попробуйте ещё раз.");
    } finally {
      setPending(false);
    }
  }

  return (
    <form onSubmit={submit} className="space-y-4">
      {!configured ? (
        <p className="rounded-xl bg-amber-400/15 px-4 py-3 text-sm text-amber-200 ring-1 ring-amber-400/30">
          Пароль администратора не настроен. Задайте переменную окружения{" "}
          <code className="font-mono">ADMIN_PASSWORD</code> и перезапустите приложение.
        </p>
      ) : null}

      <label className="block">
        <span className="text-xs font-bold uppercase tracking-wider text-emerald-200/60">
          Пароль администратора
        </span>
        <div className="relative mt-1">
          <input
            type={show ? "text" : "password"}
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            autoFocus
            autoComplete="current-password"
            placeholder="••••••••"
            aria-label="Пароль администратора"
            className="w-full rounded-xl border border-emerald-400/25 bg-emerald-950/60 px-4 py-3 pr-12 text-white outline-none placeholder:text-emerald-200/30 focus:border-emerald-300"
            required
          />
          <button
            type="button"
            onClick={() => setShow((value) => !value)}
            aria-label={show ? "Скрыть пароль" : "Показать пароль"}
            title={show ? "Скрыть пароль" : "Показать пароль"}
            className="absolute right-3 top-1/2 -translate-y-1/2 rounded-lg p-2 text-emerald-300/80 transition hover:bg-emerald-400/10 hover:text-emerald-200"
          >
            {show ? (
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className="h-5 w-5" aria-hidden="true">
                <path d="M3 3l18 18M10.6 5.1A9.7 9.7 0 0 1 12 5c5.5 0 9 5 9 7a11.6 11.6 0 0 1-2.4 3.5M6.2 6.7C4.2 8.2 3 10.4 3 12c0 2 3.5 7 9 7 1.6 0 3-.4 4.3-1.1M9.9 9.9a3 3 0 0 0 4.2 4.2" strokeLinecap="round" />
              </svg>
            ) : (
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className="h-5 w-5" aria-hidden="true">
                <path d="M3 12s3.5-7 9-7 9 7 9 7-3.5 7-9 7-9-7-9-7z" strokeLinecap="round" />
                <circle cx="12" cy="12" r="3" />
              </svg>
            )}
          </button>
        </div>
      </label>

      {error ? (
        <p className="rounded-xl bg-rose-500/15 px-4 py-3 text-sm text-rose-200 ring-1 ring-rose-400/30">
          {error}
        </p>
      ) : null}

      <button
        type="submit"
        disabled={pending || !configured}
        className="w-full rounded-full bg-emerald-400 px-6 py-3 text-sm font-black uppercase tracking-wide text-emerald-950 transition hover:bg-emerald-300 active:scale-95 disabled:opacity-60"
      >
        {pending ? "Проверяем…" : "Войти в админку"}
      </button>
    </form>
  );
}
